/*******************************************************************
* PAGE        : DropdownComponent
* REMARKS     : Comman Dropdown Component
* CREATED ON  : 03-11-2025
* AUTHOR      : Bamroliya.Paras
* MODIFY      : Kalsait.Akshay
* MODIFY ON   : 13-01-2026
********************************************************************/

import React, { useEffect, useRef, useState, useCallback, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import "./DropdownComponent.css";
import TextFieldComponent from "../TextFieldComponent/TextFieldComponent";

const DropdownComponent = ({
    label,
    options = [],
    value,
    name,
    onChange,
    placeholder,
    disabled = false,
    error,
    required,
    style,
    menuStyle,
    isForTableHeader = false,
    isSearch = false,
    ...props
}) => {
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);
    const optionRefs = useRef([]);

    const [open, setOpen] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState("");
    const [isHoveringMenu, setIsHoveringMenu] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const [menuPosition, setMenuPosition] = useState({
        top: 0,
        left: 0,
        width: 0,
    });

    const filteredOptions =
        isForTableHeader
            ? options
            : options.filter(item =>
                item.label?.toLowerCase().includes(searchText?.toLowerCase())
            );

    const updateMenuPosition = useCallback(() => {
        if (!dropdownRef.current) return;

        const rect = dropdownRef.current.getBoundingClientRect();
        const menuHeight = menuRef.current?.getBoundingClientRect().height || 200;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        const openAbove =
            spaceBelow < menuHeight && spaceAbove > spaceBelow;

        setMenuPosition({
            top: openAbove ? rect.top + window.scrollY - menuHeight - 4 : rect.bottom + window.scrollY + 4,
            left: rect.left + window.scrollX,
            width: rect.width,
            openAbove,
        });
    }, []);

    useEffect(() => {
        if (open && highlightedIndex >= 0) {
            optionRefs.current[highlightedIndex]?.scrollIntoView({
                block: "nearest",
            });
        }
    }, [highlightedIndex, open]);

    useEffect(() => {
        const selectedOption = options.find((item) => item.id === value);
        if (selectedOption) {
            setSelectedLabel(selectedOption.label);
        } else {
            setSelectedLabel(value || "");
        }
    }, [value, options]);

    useLayoutEffect(() => {
        if (open) {
            updateMenuPosition();
        }
    }, [filteredOptions.length]);

    useEffect(() => {
        if (open) {
            requestAnimationFrame(() => {
                updateMenuPosition();
            });
        }
    }, [open, filteredOptions.length]);

    useEffect(() => {
        if (open) {
            setSearchText("");
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            updateMenuPosition();
            window.addEventListener("scroll", updateMenuPosition, true);
            window.addEventListener("resize", updateMenuPosition);
        }
        return () => {
            window.removeEventListener("scroll", updateMenuPosition, true);
            window.removeEventListener("resize", updateMenuPosition);
        };
    }, [open, updateMenuPosition]);

    useEffect(() => {
        if (!open) return;

        const handleScrollClose = () => {
            if (!isHoveringMenu) {
                setOpen(false);
            }
        };

        window.addEventListener("scroll", handleScrollClose, true);

        return () => {
            window.removeEventListener("scroll", handleScrollClose, true);
        };
    }, [open, isHoveringMenu]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !event.target.closest(".dropdown-menu-portal")) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    const handleSelect = (option) => {
        onChange({ target: { value: option.id } });
        setSearchText("");
        setOpen(false);
    };

    const handleSearchChange = (e) => {
        const newVal = e.target.value;
        setSearchText(newVal);
        if (isSearch) {
            onChange({ target: { value: newVal } });
        }
    };

    const truncateText = (text, max = 30) => {
        if (!text) return "";
        return text.length > max ? `${text.substring(0, max)}...` : text;
    };

    const handleToggle = () => {
        if (disabled) return;

        if (!open) {
            setSearchText(selectedLabel); // show selected value
        } else {
            setSearchText("");
        }

        setOpen(!open);
    };

    const handleKeyDown = (e) => {
        if (disabled) return;

        // Open on ArrowDown or Enter when closed
        if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
            e.preventDefault();
            setOpen(true);
            return;
        }

        if (!open) return;

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setHighlightedIndex((prev) =>
                    prev < filteredOptions.length - 1 ? prev + 1 : 0
                );
                break;

            case "ArrowUp":
                e.preventDefault();
                setHighlightedIndex((prev) =>
                    prev > 0 ? prev - 1 : filteredOptions.length - 1
                );
                break;

            case "Enter":
                e.preventDefault();
                if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
                    handleSelect(filteredOptions[highlightedIndex]);
                }
                break;

            case "Escape":
                e.preventDefault();
                setOpen(false);
                break;

            case "Tab":
                // Let browser move focus naturally, just close menu
                setOpen(false);
                break;

            default:
                break;
        }
    };

    const handleBlur = () => {
        requestAnimationFrame(() => {
            const active = document.activeElement;

            if (
                !dropdownRef.current?.contains(active) &&
                !menuRef.current?.contains(active)
            ) {
                setOpen(false);
            }
        });
    };

    // added By Akshay
    const dropdownMenu =
        open && !disabled
            ? ReactDOM.createPortal(
                <div
                    ref={menuRef}
                    className="dropdown-menu dropdown-menu-portal"
                    onMouseEnter={() => setIsHoveringMenu(true)}
                    onMouseLeave={() => setIsHoveringMenu(false)}
                    style={{
                        ...(menuStyle || { maxHeight: "250px" }), position: "absolute", top: menuPosition.top, left: menuPosition.left, width: menuPosition.width, zIndex: 99999, boxSizing: "border-box",
                    }}
                >
                    {filteredOptions.map((option, index) => (
                        <div
                            ref={(el) => (optionRefs.current[index] = el)}
                            key={option.id}
                            className={`dropdown-item
            ${value === option.id ? "selected" : ""}
            ${highlightedIndex === index ? "highlighted" : ""}`}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            onClick={() => handleSelect(option)}
                            title={option.label}
                        >
                            {truncateText(option.label)}
                        </div>
                    ))}
                </div>,
                document.body,
            )
            : null;

    return (
        <div className="dropdown-select-group" style={style} ref={dropdownRef}>
            {/* LABEL */}
            {label && (
                <label className={`dropdown-select-label ${required ? "required" : ""} `}>
                    {label || "Select"}
                </label>
            )}

            <div
                className={`${isForTableHeader ? "list-select-box" : "dropdown-select-box"} ${open ? "active" : ""} ${error ? "error" : ""} ${disabled ? "disabled" : ""}`}
                tabIndex={disabled ? -1 : 0}
                // onKeyDown={handleKeyDown}
                onClick={handleToggle}
            >

                {isSearch && !isForTableHeader && open
                    ? <TextFieldComponent
                        autoFocus
                        value={searchText || selectedLabel}
                        name={name}
                        onKeyDown={handleKeyDown}
                        onChange={handleSearchChange}
                    />
                    : <span className={selectedLabel ? "dropdown-value" : "dropdown-placeholder"}>
                        {selectedLabel
                            ? truncateText(selectedLabel)
                            : placeholder || "Select an option"}
                    </span>
                }

                <span className={`dropdown-arrow ${open ? "open" : ""}`}></span>
            </div>

            {dropdownMenu}

            {error && <span className="error-text">{error}</span>}
        </div>
    );
};

export default DropdownComponent;